import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, normalizeClass as _normalizeClass, resolveComponent as _resolveComponent, withCtx as _withCtx, resolveDirective as _resolveDirective, withDirectives as _withDirectives, withModifiers as _withModifiers, createStaticVNode as _createStaticVNode } from "vue"

const _hoisted_1 = { class: "book-export__kindle" }
const _hoisted_2 = { class: "button-toggle-expand" }
const _hoisted_3 = {
  key: 0,
  class: "book-export__expanded-icon iconfont ikindle-collapse"
}
const _hoisted_4 = {
  key: 1,
  class: "book-export__expanded-icon iconfont ikindle-expand"
}
const _hoisted_5 = {
  key: 0,
  class: "book-export__kindle-item"
}
const _hoisted_6 = { class: "book-export__copy-email" }
const _hoisted_7 = {
  key: 1,
  class: "book-export__kindle-item"
}
const _hoisted_8 = {
  key: 2,
  class: "book-export__kindle-item"
}
const _hoisted_9 = { class: "book-export__kindle-email-box" }
const _hoisted_10 = { class: "book-export__kindle-label-wrapper" }
const _hoisted_11 = ["disabled"]

import { computed, onBeforeMount, ref } from 'vue'
import { useWait } from '@/app/composables/useWait'

import { Sentry } from '@/services/sentry'

import FormError from '@/components/ui/FormError.vue'
import { ValidationState } from '@/helpers/form'
import { assertNotNull } from '@/helpers/typing'

import { BookView } from '@/models/doc.book'
import { backend } from '@/services/backend'
import InputWithContextMenu from '@/app/components/InputWithContextMenu.vue'
import KindleScreenshotPopup from './KindleScreenshotPopup.vue'
import KindleNotification from './KindleNotification.vue'
import CopyMessagePopup from './CopyMessagePopup.vue'

const shortFormEmail = 'export@shortform.com'


export default /*@__PURE__*/_defineComponent({
  __name: 'KindleExportForm',
  props: {
    book: {}
  },
  setup(__props: any) {

const props = __props

const wait = useWait()

const validation = ref(new ValidationState())
const isExpanded = ref(true)
const showScreenshotPopup = ref(false)
const showNotification = ref(false)
const showCopyNotification = ref(false)

const kindleEmail = ref<string | null>(null)

onBeforeMount(() => {
  // If the user already has a kindle_email set, don't show the instructions
  // and fill in the Kindle email form field.
  if (props.book.doc_export_stats && props.book.doc_export_stats.kindle_email) {
    isExpanded.value = false
    kindleEmail.value = props.book.doc_export_stats.kindle_email
  }
  // For now, force the settings block to be expanded, so users
  // see the updated email in the instructions, see #12087.
  isExpanded.value = true
})

function getErrorMessage(error: any): string {
  if (error.response === undefined) {
    Sentry.captureException(error)
    return 'Could not talk to backend. Network or authorisation error?'
  } else {
    // The response structure is that of marshmallow.exceptions.ValidationError:
    //   {errors: {field_name: [error_msg, ...], ...}}
    // field_name may be
    // - `_schema`: default, for a general schema-level error. But we don't
    //   expect any errors like this.
    // - an actual field. For this, the fields we may expect errors from are
    //   `user`, `url_slug`, and `kindle_email`.
    const knownFieldNames = ['_schema', 'user', 'url_slug', 'kindle_email']
    const knownErrorMessages = new Set([
      'Doc export is not available for free users',
      'Doc export is not available during trial',
      'Export limit reached',
      "We didn't find this summary in our database.",
      'The given email does not end with @kindle.com.',
      'No Send to Kindle email given.',
    ])

    // If we find an error message we recognize, don't send the error to
    // Sentry since the user can deal with it.
    const maybeErrors = error?.response?.data?.errors
    if (maybeErrors) {
      for (const fieldName of knownFieldNames) {
        if (fieldName in maybeErrors) {
          for (const errorMessage of maybeErrors[fieldName]) {
            if (knownErrorMessages.has(errorMessage)) {
              return errorMessage
            }
          }
        }
      }
    }

    // Otherwise, it is probably not useful to show the user the specific
    // error, and we should capture it in Sentry instead.
    Sentry.captureException(error)
    return 'Error sending to Kindle. Please contact support.'
  }
}

async function sendToBackend(): Promise<void> {
  try {
    validation.value.reset()
    wait.start('Sending')
    const email = assertNotNull(kindleEmail.value)
    await backend.sendToKindle(props.book.url_slug, email)
    toggleNotification()
  } catch (error) {
    validation.value.showMessage('kindle_email', getErrorMessage(error))
  } finally {
    wait.end('Sending')
  }
}

function toggleExpand(): void {
  isExpanded.value = !isExpanded.value
}

function toggleScreenshot(): void {
  showScreenshotPopup.value = !showScreenshotPopup.value
}

function toggleNotification(): void {
  showNotification.value = !showNotification.value

  if (showNotification.value) {
    setTimeout(() => {
      showNotification.value = false
    }, 5000)
  }
}

const supportsCopy = computed(() => {
  return navigator.clipboard !== undefined
})

function copy(): void {
  navigator.clipboard.writeText(shortFormEmail)
  showCopyNotification.value = !showCopyNotification.value

  setTimeout(() => {
    showCopyNotification.value = false
  }, 2000)
}

return (_ctx: any,_cache: any) => {
  const _component_button_spinner = _resolveComponent("button-spinner")!
  const _component_v_wait = _resolveComponent("v-wait")!
  const _directive_wait = _resolveDirective("wait")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _cache[12] || (_cache[12] = _createElementVNode("h2", { class: "book-export__action-title" }, [
      _createElementVNode("span", { class: "book-export__title-arrow iconfont iarrow" }),
      _createTextVNode("Send to my Kindle device ")
    ], -1)),
    _cache[13] || (_cache[13] = _createElementVNode("p", { class: "book-export__action-description" }, " Read on your favorite Kindle device with all Kindle features supported. ", -1)),
    _createElementVNode("div", {
      class: "book-export__kindle-item book-export__kindle-header",
      onClick: toggleExpand
    }, [
      _cache[1] || (_cache[1] = _createElementVNode("span", { class: "book-export__kindle-title" }, "How to integrate", -1)),
      _createElementVNode("button", _hoisted_2, [
        (isExpanded.value)
          ? (_openBlock(), _createElementBlock("span", _hoisted_3))
          : (_openBlock(), _createElementBlock("span", _hoisted_4))
      ])
    ]),
    (isExpanded.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
          _cache[4] || (_cache[4] = _createElementVNode("div", { class: "book-export__kindle-copy-instruction" }, [
            _createElementVNode("strong", null, "Step 1"),
            _createTextVNode(": Copy our verified address ")
          ], -1)),
          _createElementVNode("div", _hoisted_6, [
            _cache[3] || (_cache[3] = _createElementVNode("input", {
              class: "book-export__kindle-input",
              type: "text",
              readonly: "",
              value: "export@shortform.com"
            }, null, -1)),
            (supportsCopy.value)
              ? (_openBlock(), _createElementBlock("button", {
                  key: 0,
                  class: "book-export__copy-button",
                  onClick: copy
                }, [
                  _cache[2] || (_cache[2] = _createElementVNode("span", { class: "book-export__copy-icon iconfont ikindle-copy" }, null, -1)),
                  _createVNode(CopyMessagePopup, { show: showCopyNotification.value }, null, 8, ["show"])
                ]))
              : _createCommentVNode("", true)
          ])
        ]))
      : _createCommentVNode("", true),
    (isExpanded.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
          _cache[7] || (_cache[7] = _createStaticVNode("<strong class=\"book-export__kindle-item-title\">Step 2:</strong><p class=\"book-export__kindle-row\"> &gt; Go to your <a class=\"book-export__kindle-link book-export__preference-link\" href=\"https://www.amazon.com/hz/mycd/myx#/home/settings/payment#pdoc\" target=\"_blank\" rel=\"noopener noreferrer\">Kindle preferences page</a>. </p><p class=\"book-export__kindle-row\"> &gt; Expand &quot;<strong>Personal Document Settings</strong>&quot;. </p><p class=\"book-export__kindle-row\"> &gt; Click the &quot;<strong>Add a new approved e-mail address</strong>&quot; link. </p><p class=\"book-export__kindle-row\"> &gt; Add <strong>export@shortform.com</strong> to your approved e-mail list. </p>", 5)),
          _createElementVNode("p", { class: "book-export__kindle-row book-export__kindle-hint" }, [
            _cache[5] || (_cache[5] = _createTextVNode(" Open ", -1)),
            _createElementVNode("button", {
              class: "book-export__kindle-link book-export__button-screenshot",
              onClick: toggleScreenshot
            }, " screenshot "),
            _cache[6] || (_cache[6] = _createTextVNode(" for help ", -1))
          ])
        ]))
      : _createCommentVNode("", true),
    (isExpanded.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_8, [...(_cache[8] || (_cache[8] = [
          _createStaticVNode("<strong class=\"book-export__kindle-item-title\">Step 3:</strong><p class=\"book-export__kindle-row\"> &gt; Go to your <a class=\"book-export__kindle-link book-export__preference-link\" href=\"https://www.amazon.com/hz/mycd/myx#/home/settings/payment#pdoc\" target=\"_blank\" rel=\"noopener noreferrer\">Kindle preferences page</a>. </p><p class=\"book-export__kindle-row\"> &gt; Expand &quot;<strong>Personal Document Settings</strong>&quot;. </p><p class=\"book-export__kindle-row\"> &gt; Find the &quot;<strong>Send-to-Kindle E-Mail Settings</strong>&quot; section. </p><p class=\"book-export__kindle-row\"> &gt; Copy and paste your personal Kindle email to the field below. </p>", 5)
        ]))]))
      : _createCommentVNode("", true),
    _createElementVNode("form", {
      role: "form",
      class: "form",
      novalidate: "",
      onSubmit: _withModifiers(sendToBackend, ["prevent"])
    }, [
      _createElementVNode("div", _hoisted_9, [
        _createElementVNode("div", _hoisted_10, [
          _cache[9] || (_cache[9] = _createElementVNode("label", {
            class: "book-export__kindle_subtitle",
            for: "kindleEmail"
          }, " Your personal Kindle email ", -1)),
          _createVNode(FormError, {
            errors: validation.value.errors,
            field: "kindle_email"
          }, null, 8, ["errors"])
        ]),
        _createVNode(InputWithContextMenu, {
          ref: "kindle_email",
          modelValue: kindleEmail.value,
          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((kindleEmail).value = $event)),
          name: "kindleEmail",
          type: "text",
          placeholder: "@kindle.com",
          class: _normalizeClass(validation.value.stateClass('kindle_email'))
        }, null, 8, ["modelValue", "class"])
      ]),
      _withDirectives((_openBlock(), _createElementBlock("button", {
        class: _normalizeClass(["btns kindle-button", { 'book-export__disabled': !kindleEmail.value }]),
        type: "submit",
        disabled: !kindleEmail.value
      }, [
        _createVNode(_component_v_wait, { for: "Sending" }, {
          waiting: _withCtx(() => [
            _cache[10] || (_cache[10] = _createTextVNode(" Sending ", -1)),
            _createVNode(_component_button_spinner)
          ]),
          default: _withCtx(() => [
            _cache[11] || (_cache[11] = _createTextVNode(" Send to Kindle ", -1))
          ]),
          _: 1
        })
      ], 10, _hoisted_11)), [
        [_directive_wait, 'Sending', "disabled"]
      ])
    ], 32),
    _createVNode(KindleScreenshotPopup, {
      show: showScreenshotPopup.value,
      onToggleShow: toggleScreenshot
    }, null, 8, ["show"]),
    _createVNode(KindleNotification, {
      show: showNotification.value,
      onToggleShow: toggleNotification
    }, null, 8, ["show"])
  ]))
}
}

})